// src/utils/workspaceGate.ts
import { API_URL, isLocalHost, isWorkspaceHost } from "./runtimeConfig";
import { getIdTokenFromCookie } from "./getIdToken";
import { tenantSlug } from "./tenantSlug";
import { tenantHost } from "./runtimeConfig";

export function getTenantSlugFromHostname(hostname: string, suffix: string) {
  const host = hostname.split(":")[0];

  if (!host.endsWith(suffix)) return null;

  const left = host.slice(0, host.length - suffix.length);
  if (!left) return null;

  if (left.includes(".")) return null;

  return left;
}

function base64UrlDecodeToJson(b64url: string): any {
  const b64 = String(b64url || "").replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";

  const binary = atob(b64 + pad);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  const json = new TextDecoder("utf-8").decode(bytes);

  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getTenantFromIdToken(idToken: string): string {
  const parts = String(idToken || "").split(".");
  if (parts.length < 2) return "";
  const payload = base64UrlDecodeToJson(parts[1]);
  if (!payload) return "";
  const claim = payload["custom:company"] || "";

  const raw = String(claim || "");
  const slug = tenantSlug(raw);

  return slug;
}

export async function enforceWorkspaceTenant(opts?: {
  workspaceSuffix?: string;
  publicRedirectHost?: string;
}) {
  if (typeof window === "undefined") return;

  const hostname = window.location.hostname;
  const path = window.location.pathname || "";

  const suffix = opts?.workspaceSuffix ?? ".bimeco.io";
  const publicHost = opts?.publicRedirectHost ?? "bimeco.io";

  if (path.startsWith("/user/") || path.startsWith("/auth/") || path === "/logout") {
    return;
  }

  // Local + non-workspace: don't gate
  if (isLocalHost(hostname)) return;
  if (!isWorkspaceHost(hostname)) return;

  const hostTenantRaw = getTenantSlugFromHostname(hostname, suffix);
  const hostTenant = tenantSlug(hostTenantRaw || "");

  if (!hostTenant) {
    window.location.replace(`https://${publicHost}/`);
    return;
  }

  try {
    const r = await fetch(`${API_URL}/brand/${encodeURIComponent(hostTenant)}`, {
      method: "GET",
      cache: "no-store",
    });
    if (!r.ok) {
      window.location.replace(`https://${publicHost}/`);
      return;
    }
  } catch {
    window.location.replace(`https://${publicHost}/`);
    return;
  }

  // 2) must be logged in
  const idToken = getIdTokenFromCookie();
  if (!idToken) {
    window.location.replace(`/user/login`);
    return;
  }

  // 3) must match tenant in token
  const tokenTenant = getTenantFromIdToken(idToken);
  if (!tokenTenant) {
    window.location.replace(`/user/login`);
    return;
  }

  if (tokenTenant !== hostTenant) {
    const targetHost = tenantHost(tokenTenant);
    window.location.replace(`https://${targetHost}/`);
    return;
  }

  return;
}